import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import lightLogoMark from '../../assets/navbar-logo-mark.svg';
import lightLogoWordmark from '../../assets/navbar-logo-wordmark.svg';
import darkLogoMark from '../../assets/navbar-logo-mark-dark.svg';
import darkLogoWordmark from '../../assets/navbar-logo-wordmark-dark.svg';
import chevronDown from '../../assets/icons/chevron-down.svg';
import hamburgerIcon from '../../assets/icons/hamburger.svg';
import './navbar-new.css';

const portfolioLinks = [
  { label: 'Telecommunication', to: '/portfolio/telecommunication' },
  { label: 'Real Estate & Construction', to: '/portfolio/real-estate' },
  { label: 'Interior Design', to: '/portfolio/interior-design' },
  { label: 'Business Processing Outsource', to: '/portfolio/bpo' },
  { label: 'Food & Hospitality', to: '/portfolio/food-hospitality' },
  { label: 'Import & Export', to: '/portfolio/import-export' },
  { label: 'Automotive', to: '/portfolio/automotive' },
  { label: 'Fashion & Apparel', to: '/portfolio/fashion-apparel' },
  { label: 'Philanthropy', to: '/portfolio/philanthropy' },
];

const lightBackgroundRoutes = new Set([
  '/portfolio/telecommunication',
  '/portfolio/real-estate',
  '/portfolio/interior-design',
  '/portfolio/bpo',
  '/portfolio/food-hospitality',
  '/portfolio/import-export',
  '/portfolio/automotive',
  '/portfolio/fashion-apparel',
  '/portfolio/philanthropy',
]);

const Navbar = () => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobilePortfolioOpen, setMobilePortfolioOpen] = useState(false);
  const dropdownRef = useRef(null);
  const location = useLocation();
  const isLightBackgroundPage = lightBackgroundRoutes.has(location.pathname);
  const navbarThemeClass = isLightBackgroundPage ? 'kw__navbar--on-light' : 'kw__navbar--on-dark';

  useEffect(() => {
    setMobileOpen(false);
    setDropdownOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  return (
    <nav className={`kw__navbar ${navbarThemeClass}`}>
      <div className="kw__navbar_inner">
        <Link to="/" className="kw__navbar_logo">
          {isLightBackgroundPage ? (
            <div className="kw__navbar_logo-figma kw__navbar_logo-figma--light" aria-label="Kothawala Group">
              <img src={lightLogoMark} alt="" className="kw__navbar_logo-mark" />
              <img src={lightLogoWordmark} alt="" className="kw__navbar_logo-wordmark" />
            </div>
          ) : (
            <div className="kw__navbar_logo-figma kw__navbar_logo-figma--dark" aria-label="Kothawala Group">
              <img src={darkLogoMark} alt="" className="kw__navbar_logo-mark" />
              <img src={darkLogoWordmark} alt="" className="kw__navbar_logo-wordmark" />
            </div>
          )}
        </Link>

        <div className="kw__navbar_pill">
          <div className="kw__navbar_links">
            <div
              className={`kw__navbar_dropdown${dropdownOpen ? ' open' : ''}`}
              ref={dropdownRef}
            >
              <button
                type="button"
                className="kw__navbar_link"
                onClick={() => setDropdownOpen((v) => !v)}
              >
                Our Portfolio
              </button>
              <img
                src={chevronDown}
                alt=""
                className="kw__navbar_dropdown-icon"
              />
              <div className="kw__navbar_dropdown-menu">
                <Link to="/portfolio" className="kw__navbar_dropdown-item">
                  View All
                </Link>
                {portfolioLinks.map((item) => (
                  <Link
                    key={item.to}
                    to={item.to}
                    className="kw__navbar_dropdown-item"
                  >
                    {item.label}
                  </Link>
                ))}
              </div>
            </div>

            <Link to="/sell-your-business" className="kw__navbar_link">
              Sell Your Business
            </Link>
            <Link to="/whats-new" className="kw__navbar_link">
              What&apos;s New?
            </Link>
            <Link to="/investors" className="kw__navbar_link">
              Investors
            </Link>
            <Link to="/careers" className="kw__navbar_link">
              Join the Team
            </Link>
          </div>

          <button
            type="button"
            className="kw__navbar_hamburger"
            onClick={() => setMobileOpen(true)}
            aria-label="Open menu"
          >
            <img src={hamburgerIcon} alt="" />
          </button>
        </div>
      </div>

      {/* Mobile overlay */}
      <div
        className={`kw__navbar_mobile-overlay${mobileOpen ? ' open' : ''}`}
        onClick={() => setMobileOpen(false)}
        role="presentation"
      />

      {/* Mobile menu */}
      <div className={`kw__navbar_mobile-menu${mobileOpen ? ' open' : ''}`}>
        <button
          type="button"
          className="kw__navbar_mobile-close"
          onClick={() => setMobileOpen(false)}
          aria-label="Close menu"
        >
          ✕
        </button>
        <div className="kw__navbar_mobile-links">
          <Link to="/" className="kw__navbar_mobile-link">Home</Link>
          <button
            type="button"
            className="kw__navbar_mobile-link"
            onClick={() => setMobilePortfolioOpen((v) => !v)}
            style={{ background: 'none', border: 'none', textAlign: 'left', cursor: 'pointer', width: '100%' }}
          >
            Our Portfolio {mobilePortfolioOpen ? '−' : '+'}
          </button>
          {mobilePortfolioOpen && (
            <div className="kw__navbar_mobile-sub">
              <Link to="/portfolio" className="kw__navbar_mobile-link">View All</Link>
              {portfolioLinks.map((item) => (
                <Link key={item.to} to={item.to} className="kw__navbar_mobile-link">
                  {item.label}
                </Link>
              ))}
            </div>
          )}
          <Link to="/sell-your-business" className="kw__navbar_mobile-link">Sell Your Business</Link>
          <Link to="/whats-new" className="kw__navbar_mobile-link">What&apos;s New?</Link>
          <Link to="/investors" className="kw__navbar_mobile-link">Investors</Link>
          <Link to="/careers" className="kw__navbar_mobile-link">Join the Team</Link>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
